import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useSettings } from '@/store/settingsStore';
import { ANIMATION_DURATION } from '@/ui/lib/constants';
import { queryClient } from '@/ui/lib/queryClient';
import { RacerSelection } from '@/ui/components/RacerSelection/RacerSelection';
import { BottomBar } from '@/ui/components/BottomBar/BottomBar';
import { RaceResults } from '@/ui/components/RaceResults/RaceResults';
import { TopBar } from '@/ui/components/TopBar/TopBar';
import { Racing } from '@/ui/components/Racing/Racing';
import { Landing } from '@/ui/components/Landing/Landing';
import { Menu } from '@/ui/components/Menu/Menu';
import { DelayedRender } from './components/base/DelayedRender/DelayedRender';
import { RotateDevice } from './components/RotateDevice/RotateDevice';
import { useOrientationLock } from './hooks/useOrientationLock';
import { useStore } from '@/store/useStore';
import './styles/index.css';

export const UI = () => {
    const gameState = useStore((state) => state.gameState);
    const showRotate = useOrientationLock();
    const routeRef = useRef<HTMLDivElement>(null);
    const skipLanding = useSettings((state) => state.skipLanding);
    const [gameStarted, setGameStarted] = useState(() => skipLanding);

    const startGame = useCallback(() => {
        setGameStarted(true);
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <div className="ui">
                <TopBar />

                <Menu />

                <SwitchTransition>
                    <CSSTransition
                        key={gameState}
                        nodeRef={routeRef}
                        timeout={ANIMATION_DURATION}
                        unmountOnExit
                        classNames="fade"
                    >
                        <main className="ui__main" ref={routeRef}>
                            {gameState === 'PLACE_BET' && <RacerSelection />}
                            {gameState === 'RACE' && <Racing />}
                            {gameState === 'CELEBRATION' && (
                                <DelayedRender delay={1500}>
                                    <RaceResults />
                                </DelayedRender>
                            )}
                        </main>
                    </CSSTransition>
                </SwitchTransition>

                <BottomBar />
            </div>

            {!gameStarted && <Landing startGame={startGame} />}

            {showRotate && <RotateDevice />}

            {createPortal(<ReactQueryDevtools />, document.getElementById('react-query-devtools')!)}
        </QueryClientProvider>
    );
};
